import React, { useState } from 'react';
import {
  Form, Table, Badge, Button, Collapse,
} from 'react-bootstrap';

const MODES = [
  { value: 'not_set', label: 'Not set' },
  { value: 'override', label: 'Override' },
  { value: 'opt_out', label: 'Opt out' },
];

function OperatorRouteOverridesTable({
  predefinedRoutes = [],
  routeOverrides = [],
  vehicleCategories = [],
  currency,
  onChange,
}) {
  const [expandedRouteId, setExpandedRouteId] = useState(null);

  const overridesById = new Map(
    routeOverrides.map((ro) => [ro.predefined_route_id, ro]),
  );

  const setMode = (routeId, mode) => {
    const next = routeOverrides.filter((ro) => ro.predefined_route_id !== routeId);
    if (mode === 'opt_out') {
      next.push({ predefined_route_id: routeId, mode: 'opt_out' });
    } else if (mode === 'override') {
      next.push({
        predefined_route_id: routeId,
        mode: 'override',
        category_prices: [],
      });
      setExpandedRouteId(routeId);
    }
    onChange(next);
  };

  const setCategoryPrice = (routeId, categoryId, price) => {
    const next = routeOverrides.map((ro) => {
      if (ro.predefined_route_id !== routeId) return ro;
      const categoryPrices = [...(ro.category_prices || [])];
      const idx = categoryPrices.findIndex((cp) => cp.vehicle_category_id === categoryId);
      if (price === '' || price === null || price === undefined) {
        if (idx >= 0) categoryPrices.splice(idx, 1);
      } else if (idx >= 0) {
        categoryPrices[idx] = { vehicle_category_id: categoryId, price: Number(price) };
      } else {
        categoryPrices.push({ vehicle_category_id: categoryId, price: Number(price) });
      }
      return { ...ro, category_prices: categoryPrices };
    });
    onChange(next);
  };

  const optOutAll = () => {
    onChange(
      predefinedRoutes.map((r) => ({ predefined_route_id: r.Id, mode: 'opt_out' })),
    );
  };

  const clearAll = () => {
    onChange([]);
  };

  const activeRoutes = predefinedRoutes.filter((r) => r.active === 1);
  const activeCategories = vehicleCategories.filter((c) => c.active === 1);
  const notSetCount = activeRoutes.filter(
    (r) => !overridesById.has(r.Id),
  ).length;

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h5 className="mb-0">Route Overrides</h5>
          {notSetCount > 0 ? (
            <Badge bg="warning" text="dark">
              {`${notSetCount} not set — quotes will fail for trips matching these routes`}
            </Badge>
          ) : (
            <Badge bg="success">All routes configured</Badge>
          )}
        </div>
        <div className="d-flex gap-2">
          <Button variant="outline-warning" size="sm" onClick={optOutAll}>
            Opt out of all routes
          </Button>
          <Button variant="outline-secondary" size="sm" onClick={clearAll}>
            Reset all to Not set
          </Button>
        </div>
      </div>

      <Table responsive bordered size="sm">
        <thead className="table-light">
          <tr>
            <th>Route</th>
            <th style={{ width: '160px' }}>Mode</th>
            <th style={{ width: '140px' }}>Prices</th>
          </tr>
        </thead>
        <tbody>
          {activeRoutes.map((route) => {
            const override = overridesById.get(route.Id);
            const mode = override?.mode ?? 'not_set';
            const isExpanded = expandedRouteId === route.Id && mode === 'override';
            const pricesByCat = new Map(
              (override?.category_prices || []).map((cp) => [cp.vehicle_category_id, cp.price]),
            );
            return (
              <React.Fragment key={route.Id}>
                <tr>
                  <td>
                    <strong>{route.name}</strong>
                    {route.is_bidirectional && (
                      <Badge bg="info" className="ms-2">Both ways</Badge>
                    )}
                  </td>
                  <td>
                    <Form.Select
                      size="sm"
                      aria-label={`Mode for ${route.name}`}
                      value={mode}
                      onChange={(e) => setMode(route.Id, e.target.value)}
                    >
                      {MODES.map((m) => (
                        <option key={m.value} value={m.value}>
                          {m.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                  <td>
                    {mode === 'override' ? (
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => setExpandedRouteId(isExpanded ? null : route.Id)}
                      >
                        <i className={`fa fa-chevron-${isExpanded ? 'up' : 'down'} me-1`} />
                        {`${(override?.category_prices || []).length} priced`}
                      </Button>
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
                {mode === 'override' && (
                  <tr>
                    <td colSpan={3} className="p-0 border-0">
                      <Collapse in={isExpanded}>
                        <div className="p-3 bg-light border-bottom">
                          <Table size="sm" bordered className="mb-0">
                            <thead>
                              <tr>
                                <th>Vehicle category</th>
                                <th>{`Price${currency ? ` (${currency})` : ''}`}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {activeCategories.map((cat) => (
                                <tr key={cat.Id}>
                                  <td>{cat.name}</td>
                                  <td>
                                    <Form.Control
                                      type="number"
                                      size="sm"
                                      step="0.01"
                                      min={0}
                                      value={pricesByCat.get(cat.Id) ?? ''}
                                      onChange={(e) => (
                                        setCategoryPrice(route.Id, cat.Id, e.target.value)
                                      )}
                                      placeholder="Leave empty to skip this category"
                                    />
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </Table>
                        </div>
                      </Collapse>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
          {activeRoutes.length === 0 && (
            <tr>
              <td colSpan={3} className="text-center text-muted">
                No active predefined routes
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  );
}

export default OperatorRouteOverridesTable;
